import React, { useState, useEffect } from 'react';
import { uploadFile, listFiles, getAllFines, createAppeal, getAppeals } from '../api';
import './FineManagement.css';

const PAGE_SIZE = 50;

function FineManagement() {
  const [fines, setFines]                   = useState([]);
  const [filteredFines, setFilteredFines]   = useState([]);
  const [lastProcessedDate, setLastProcessedDate] = useState(null);
  const [lastProcessedFile, setLastProcessedFile] = useState(null);
  const [totalFines, setTotalFines]         = useState(0);
  const [uploading, setUploading]           = useState(false);
  const [processedFiles, setProcessedFiles] = useState([]);
  const [filters, setFilters]               = useState({
    startDate: '', endDate: '', linha: '', codigos: [], recursoStatus: ''
  });
  const [appeals, setAppeals]               = useState([]);
  const [availableCodigos, setAvailableCodigos] = useState([]);
  const [codigoDropdownOpen, setCodigoDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage]       = useState(1);

  const empresaId = localStorage.getItem('empresaId') || 'default';

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const codigos = [...new Set(fines.map(f => f.codigo).filter(Boolean))].sort();
    setAvailableCodigos(codigos);
  }, [fines]);

  useEffect(() => { applyFilters(); }, [fines, filters, appeals]);

  useEffect(() => { setCurrentPage(1); }, [filteredFines]);

  const loadData = async () => {
    try {
      const [finesData, files, appealsData] = await Promise.all([
        getAllFines(empresaId),
        listFiles(empresaId),
        getAppeals(empresaId)
      ]);

      setFines(finesData);
      setTotalFines(finesData.length);
      setAppeals(appealsData);
      setProcessedFiles(files.map(f => f.filename || f.metadata?.source_file).filter(Boolean));

      if (files.length > 0) {
        const sorted = files.sort((a, b) =>
          new Date(b.processado_em || b.metadata?.processed_date) -
          new Date(a.processado_em || a.metadata?.processed_date)
        );
        setLastProcessedDate(sorted[0].processado_em || sorted[0].metadata?.processed_date);
        setLastProcessedFile(sorted[0].filename || sorted[0].metadata?.source_file);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setFines([]);
    }
  };

  const isExpired = (fine) => {
    if (!fine.deadline) return false;
    const [d, m, y] = fine.deadline.split('/');
    return new Date(y, m - 1, d) < new Date();
  };

  const getAppealStatus = (fine) => {
    const appeal = appeals.find(a => a.auto === fine.auto);
    if (appeal) return 'created';
    return 'available';
  };

  const handleAppealAction = async (fine) => {
    const status = getAppealStatus(fine);
    const appeal = appeals.find(a => a.auto === fine.auto);

    if (status === 'created' && appeal) {
      const processData = encodeURIComponent(JSON.stringify({
        ...fine,
        id:          appeal.id,
        orgao:       fine.concessionaria,
        fase:        appeal.fase || 'defesa_previa',
        status:      appeal.status,
        responsavel: '',
        prioridade:  'Normal',
        prazo:       fine.deadline,
        deadlineExpired: isExpired(fine)
      }));
      window.open(`${window.location.origin}${window.location.pathname}?process=${processData}`, '_blank');

    } else if (status === 'available') {
      try {
        await createAppeal(empresaId, fine.auto, 'defesa_previa');
        await loadData();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const applyFilters = () => {
    let filtered = [...fines];

    if (filters.startDate && filters.endDate) {
      if (new Date(filters.startDate) > new Date(filters.endDate)) {
        alert('Data de início não pode ser posterior à data de fim');
        setFilteredFines(fines); return;
      }
    }
    if (filters.startDate) {
      filtered = filtered.filter(f => {
        if (!f.data) return false;
        const [d, m, y] = f.data.split('/');
        return new Date(y, m - 1, d) >= new Date(filters.startDate);
      });
    }
    if (filters.endDate) {
      filtered = filtered.filter(f => {
        if (!f.data) return false;
        const [d, m, y] = f.data.split('/');
        return new Date(y, m - 1, d) <= new Date(filters.endDate);
      });
    }
    if (filters.linha) {
      filtered = filtered.filter(f =>
        f.linha && f.linha.toLowerCase().includes(filters.linha.toLowerCase())
      );
    }
    if (filters.codigos.length > 0) {
      filtered = filtered.filter(f => f.codigo && filters.codigos.includes(f.codigo));
    }
    if (filters.recursoStatus) {
      filtered = filtered.filter(f => {
        const expired = isExpired(f);
        const appeal  = appeals.find(a => a.auto === f.auto);
        if (filters.recursoStatus === 'created')   return !!appeal;
        if (filters.recursoStatus === 'available') return !appeal && !expired;
        if (filters.recursoStatus === 'expired')   return !appeal && expired;
        return true;
      });
    }

    setFilteredFines(filtered);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (processedFiles.includes(file.name)) {
      alert(`O arquivo "${file.name}" já foi processado anteriormente.`);
      e.target.value = ''; return;
    }
    setUploading(true);
    try {
      await uploadFile(file, empresaId);
      await loadData();
    } catch (err) {
      alert(err.message);
    }
    setUploading(false);
    e.target.value = '';
  };

  const clearFilters = () => setFilters({ startDate: '', endDate: '', linha: '', codigos: [], recursoStatus: '' });

  const handleCodigoToggle = (codigo) => {
    setFilters(prev => ({
      ...prev,
      codigos: prev.codigos.includes(codigo)
        ? prev.codigos.filter(c => c !== codigo)
        : [...prev.codigos, codigo]
    }));
  };

  // Paginação
  const totalPages  = Math.ceil(filteredFines.length / PAGE_SIZE);
  const pagedFines  = filteredFines.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="fine-management">
      <div className="upload-card">
        <h2>Adicionar Multas</h2>
        {lastProcessedDate && (
          <div className="metadata">
            <p><strong>Último processamento:</strong> {new Date(lastProcessedDate).toLocaleString('pt-BR')}</p>
            {lastProcessedFile && <p><strong>Arquivo:</strong> {lastProcessedFile}</p>}
            <p><strong>Total de multas no sistema:</strong> {totalFines}</p>
          </div>
        )}
        <label className="upload-btn">
          <input type="file" accept=".pdf" onChange={handleFileUpload} disabled={uploading} />
          {uploading ? 'Processando...' : '📄 Selecionar PDF'}
        </label>
      </div>

      <div className="filters-card">
        <h3>Filtros</h3>
        <div className="filters-grid">
          <div className="filter-item">
            <label>Data Início:</label>
            <input type="date" value={filters.startDate}
              max={filters.endDate || undefined}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})} />
          </div>
          <div className="filter-item">
            <label>Data Fim:</label>
            <input type="date" value={filters.endDate}
              min={filters.startDate || undefined}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})} />
          </div>
          <div className="filter-item">
            <label>Linha:</label>
            <input type="text" placeholder="Buscar linha..."
              value={filters.linha}
              onChange={(e) => setFilters({...filters, linha: e.target.value})} />
          </div>
          <div className="filter-item">
            <label>Status Recurso:</label>
            <select value={filters.recursoStatus}
              onChange={(e) => setFilters({...filters, recursoStatus: e.target.value})}>
              <option value="">Todos</option>
              <option value="available">Disponível</option>
              <option value="created">Recurso Criado</option>
              <option value="expired">Prazo Expirado</option>
            </select>
          </div>
          <div className="filter-item">
            <label>Código da Infração:</label>
            <div className="codigo-dropdown">
              <button type="button" className="dropdown-toggle"
                onClick={() => setCodigoDropdownOpen(!codigoDropdownOpen)}>
                {filters.codigos.length > 0 ? `${filters.codigos.length} selecionado(s)` : 'Selecione...'}
              </button>
              {codigoDropdownOpen && (
                <div className="dropdown-menu">
                  {availableCodigos.map(codigo => (
                    <label key={codigo} className="dropdown-item">
                      <input type="checkbox"
                        checked={filters.codigos.includes(codigo)}
                        onChange={() => handleCodigoToggle(codigo)} />
                      <span>{codigo}</span>
                    </label>
                  ))}
                  <button type="button" className="dropdown-close"
                    onClick={() => setCodigoDropdownOpen(false)}>Fechar</button>
                </div>
              )}
            </div>
          </div>
          <div className="filter-item">
            <button onClick={clearFilters} className="clear-btn">Limpar Filtros</button>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-header">
          <h2>Multas Processadas ({filteredFines.length})</h2>
          {totalPages > 1 && (
            <div className="pagination">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}>←</button>
              <span>Página {currentPage} de {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}>→</button>
            </div>
          )}
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Auto</th>
                <th>Concessionária</th>
                <th>Linha</th>
                <th>Placa</th>
                <th>Data</th>
                <th>Valor</th>
                <th>Prazo Recurso</th>
                <th>Status Recurso</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pagedFines.map((fine, idx) => {
                const appealStatus = getAppealStatus(fine);
                const expired      = isExpired(fine);
                return (
                  <tr key={idx}>
                    <td>{fine.auto}</td>
                    <td>{fine.concessionaria}</td>
                    <td>{fine.linha}</td>
                    <td>{fine.placa || '-'}</td>
                    <td>{fine.data}</td>
                    <td>{fine.valor}</td>
                    <td>
                      {fine.deadline ? (
                        <span style={{ color: expired ? '#f87171' : 'inherit' }}>
                          {fine.deadline} {expired && '⚠️'}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {appealStatus === 'created' && (
                        <span className="appeal-status appeal-created">Recurso Criado</span>
                      )}
                      {appealStatus === 'available' && !expired && (
                        <span className="appeal-status appeal-available">Disponível</span>
                      )}
                      {appealStatus === 'available' && expired && (
                        <span className="appeal-status appeal-expired">Prazo Expirado</span>
                      )}
                    </td>
                    <td>
                      {appealStatus === 'created' && (
                        <button className="action-btn view-btn" onClick={() => handleAppealAction(fine)}>
                          Ver Processo
                        </button>
                      )}
                      {appealStatus === 'available' && !expired && (
                        <button className="action-btn create-btn" onClick={() => handleAppealAction(fine)}>
                          Criar Recurso
                        </button>
                      )}
                      {appealStatus === 'available' && expired && (
                        <button className="action-btn disabled-btn" disabled>
                          Prazo Expirado
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="pagination pagination-bottom">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}>← Anterior</button>
            <span>Página {currentPage} de {totalPages} — {filteredFines.length} multas</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}>Próxima →</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FineManagement;